import { useQuery } from '@apollo/client';
import { Alert, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import GridWrapper from 'lib/GridWrapper';
import { dateTimeFormat } from 'lib/formatters';
import { SortEnumType } from 'lib/gql/queryTypes';
import { FC, useMemo } from 'react';
import { crashReportsQuery } from './queries';
import { CrashReportsQuery, CrashReportsQueryVariables } from './types/queries';
import CrashReportTriggerIcon from './CrashReportTriggerIcon';
import { formatSize } from './formatters';

interface Props {
  ownerId?: string;
  legacyServerVersionId?: string;
  compact?: boolean;
}

const fullColumns: GridColDef[] = [
  {
    field: 'capturedAt',
    headerName: 'Captured',
    width: 175,
    type: 'dateTime',
    valueGetter: (params) => new Date(params.value),
    valueFormatter: (params) => dateTimeFormat(params.value.toISOString()),
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 190,
    valueGetter: (params) => params.row.server.owner.name,
  },
  {
    field: 'server',
    headerName: 'Server',
    width: 170,
    valueGetter: (params) => params.row.server.name,
  },
  {
    field: 'legacyServerVersion',
    headerName: 'Legacy Version',
    width: 140,
    valueGetter: (params) => params.row.legacyServerVersion.version,
  },
  {
    field: 'version',
    headerName: 'WebServer Version',
    width: 150,
    valueGetter: (params) => params.row.version.versionString,
  },
  {
    field: 'trigger',
    headerName: 'Trigger',
    width: 90,
    renderCell: (params) => <CrashReportTriggerIcon trigger={params.value} />,
  },
  {
    field: 'legacyServerNumber',
    headerName: 'Legacy #',
    width: 100,
  },
  {
    field: 'exitCode',
    headerName: 'Exit Code',
    width: 100,
  },
  {
    field: 'size',
    headerName: 'Size',
    width: 100,
    valueFormatter: (params) => formatSize(params.value),
  },
  {
    field: 'receivedAt',
    headerName: 'Received',
    width: 175,
    type: 'dateTime',
    valueGetter: (params) => new Date(params.value),
    valueFormatter: (params) => dateTimeFormat(params.value.toISOString()),
  },
];

const compactColumns = fullColumns.filter((column) =>
  ['capturedAt', 'server', 'legacyServerVersion', 'trigger', 'size'].includes(
    column.field
  )
);

const CrashReportGrid: FC<Props> = ({
  ownerId,
  legacyServerVersionId,
  compact = false,
}) => {
  const where = useMemo(
    () => ({
      ...(ownerId ? { server: { ownerId: { eq: ownerId } } } : {}),
      ...(legacyServerVersionId
        ? { legacyServerVersionId: { eq: legacyServerVersionId } }
        : {}),
    }),
    [legacyServerVersionId, ownerId]
  );

  const { data, loading, error } = useQuery<
    CrashReportsQuery,
    CrashReportsQueryVariables
  >(crashReportsQuery, {
    variables: {
      where,
      order: [{ capturedAt: SortEnumType.Desc }],
    },
    fetchPolicy: 'cache-and-network',
  });

  if (error) return <Alert severity="error">{error.message}</Alert>;

  const reports = data?.crashReports ?? [];

  return (
    <Box sx={{ height: compact ? 430 : '100%', minHeight: 400 }}>
      <GridWrapper
        columns={compact ? compactColumns : fullColumns}
        rows={compact ? reports.slice(0, 10) : reports}
        loading={loading}
        basePath="/admin/crash_reports/"
        initialSort={[{ field: 'capturedAt', sort: 'desc' }]}
      />
    </Box>
  );
};

export default CrashReportGrid;
